import { ToolbarProps } from "elements/base/provider";
import { Pdf } from "../schema";
import { ButtonGroup } from "react-bootstrap";
import { produceWithPatches } from "immer";
import { useState, useEffect, useRef } from "react";
import style from "./pdf-toolbar.module.css";
import DownloadIcon from "frontend/icons/download";
import PdfPageSplitIcon from "frontend/icons/pdf-page-split";
import { canvasMetadataPrefix } from "shared/util/utils";
import { FileMetadata, FileMetadataType } from "shared/datamodel/schemas/metadata";
import { useSubscribe } from "replicache-react";
import { ReadTransaction } from "@workcanvas/reflect";
import { downloadFileUsingBlob, useIsCIMReorderEnabled } from "utils/url-utils";
import { ChevronLeft, ChevronRight } from "frontend/icons/chevron";

import { placePdf } from "shared/datamodel/pdf";
import { pdfCacheService } from "frontend/services/pdf-cache-service";
import consts from "shared/consts";
import { useAtomValue } from "jotai";
import { documentIdAtom, stageRefAtom, utilsAtom } from "state-atoms";
import { fitToSelectedElements } from "frontend/canvas-controls/zoom-controls/fit-to-stage";
import { Icon, Button as ButtonCIM } from "frontend/canvas-designer-new/elements-toolbar-cim-restyle/toolbar-buttons";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { Button as ButtonStandalone } from "frontend/canvas-designer-new/elements-toolbar/toolbar-buttons";
const PAGES_PER_ROW_FOR_SPLIT = 10;

export default function PdfToolbar({ ids, elements, context }: ToolbarProps<Pdf>) {
  const { reflect, undoRedoStack } = context;
  const stageRef = useAtomValue(stageRefAtom);
  const [currentPage, setCurrentPage] = useState(elements[0]?.currentPage || 1);
  const pageToDisplay = elements[0]?.pageToDisplay;
  const [pageInputValue, setPageInputValue] = useState(String(currentPage));
  const [isSplitting, setIsSplitting] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const downloadButtonRef = useRef<HTMLButtonElement>(null);
  const splitPagesButtonRef = useRef<HTMLButtonElement>(null);
  const totalPages = elements[0]?.totalPages || 1;
  const documentIdValue = useAtomValue(documentIdAtom);
  const canvasUtils = useAtomValue(utilsAtom(documentIdValue.documentId));
  const isCIM = useIsCIMReorderEnabled();
  const newFloatersStyle = useFeatureFlag("cim-new-floaters-style");
  const newStyle = isCIM && newFloatersStyle;

  function ButtonIgnoresClassName(props: React.PropsWithChildren<React.ComponentPropsWithRef<"button">>) {
    const { children, className: _ignore, ...rest } = props;
    return <ButtonCIM {...rest}>{children}</ButtonCIM>;
  }
  const Button = newStyle ? ButtonIgnoresClassName : ButtonStandalone;

  const metadata = useSubscribe(
    reflect,
    async (tx: ReadTransaction) => {
      const element = elements[0];
      if (!element.fileId) return null;
      const metadata = await tx.get(`${canvasMetadataPrefix}${FileMetadataType}-${element.fileId.toString()}`);
      return metadata as FileMetadata | null;
    },
    null,
    [elements[0]?.fileId]
  );

  useEffect(() => {
    const firstElement = elements[0];
    if (firstElement) {
      setCurrentPage(firstElement.currentPage);
      setPageInputValue(String(firstElement.currentPage));
    }
  }, [elements]);

  useEffect(() => {
    const firstElement = elements[0];
    const customName = firstElement?.customName;
    setNameValue(customName || metadata?.fileName || "");
  }, [elements, metadata?.fileName]);

  async function handleSaveName(newName: string) {
    const trimmedName = newName.trim();
    const element = elements[0];

    const currentName = element.customName || metadata?.fileName || "";

    if (!trimmedName || trimmedName === currentName) {
      setNameValue(currentName);
      return;
    }

    if (trimmedName.length > 255) {
      setNameValue(currentName);
      return;
    }

    setNameValue(trimmedName);

    try {
      // Create patch for custom name
      const [_, patch, inverse] = produceWithPatches(element, (draft) => {
        draft.customName = trimmedName;
      });

      const doPatches = patch.length > 0 ? [{ id: ids[0], patch }] : [];
      if (doPatches.length === 0) {
        return;
      }

      const undoPatches = [{ id: ids[0], patch: inverse }];

      undoRedoStack?.addAction({
        do: () => {
          reflect?.mutate.patchCanvasEl({ changes: doPatches });
        },
        undo: () => {
          reflect?.mutate.patchCanvasEl({ changes: undoPatches });
        },
      });
    } catch (error) {
      console.error("Failed to update PDF name:", error);
      // Revert to original name on error
      setNameValue(currentName);
    }
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNameValue(e.target.value);
  };

  const handleNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleSaveName(e.target.value || "");
  };

  const handleNameKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveName(e.currentTarget.value || "");
      e.currentTarget.blur();
    }
  };

  const handleNameFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  function changePage(newPage: number) {
    if (newPage < 1 || newPage > totalPages) {
      return;
    }

    const changes = ids.map((id, index) => {
      const element = elements[index];
      const [_, patch, inverse] = produceWithPatches(element, (draft) => {
        draft.currentPage = newPage;
      });
      return { id, patch, inverse };
    });

    const doPatches = changes.filter(({ patch }) => patch.length > 0).map(({ id, patch }) => ({ id, patch }));
    if (doPatches.length === 0) {
      return;
    }

    const undoPatches = changes.map(({ id, inverse }) => ({ id, patch: inverse }));
    undoRedoStack?.addAction({
      do: () => {
        reflect?.mutate.patchCanvasEl({ changes: doPatches });
      },
      undo: () => {
        reflect?.mutate.patchCanvasEl({ changes: undoPatches });
      },
    });
  }

  function handlePreviousPage() {
    changePage(currentPage - 1);
  }

  function handleNextPage() {
    changePage(currentPage + 1);
  }

  function handlePageInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newPage = Number.parseInt(e.target.value, 10);
    if (!Number.isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
      setPageInputValue(String(currentPage));
      changePage(newPage);
    }
  }

  const handlePageInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  async function splitPDFPages() {
    // Only process if we have a single multi-page PDF selected
    if (elements.length !== 1 || totalPages <= 1 || isSplitting) {
      return;
    }

    setIsSplitting(true);

    try {
      const originalPdf = elements[0];

      // Pre-cache the PDF for all split pages
      if (metadata?.url && originalPdf.fileId) {
        // This will load and cache the PDF if not already cached
        await pdfCacheService.getPDF(originalPdf.fileId.toString(), metadata.url);
      }

      const HORIZONTAL_SPACING = 50; // Gap between PDFs
      const VERTICAL_SPACING = 50; // Gap between rows

      // Create new PDF elements for each page
      const newPdfElements: Array<{ id: string; element: Pdf }> = [];

      const baseName = originalPdf.customName || metadata?.fileName || "PDF";
      const nameWithoutExtension = baseName.replace(/\.pdf$/i, "");

      const addedElementIds: string[] = [];

      for (let page = 1; page <= totalPages; page++) {
        const pageRow = Math.floor((page - 1) / PAGES_PER_ROW_FOR_SPLIT) + 1;
        const pageColumn = (page - 1) % PAGES_PER_ROW_FOR_SPLIT;

        const scaleX = originalPdf.scaleX || 1;
        const scaleY = originalPdf.scaleY || 1;
        const verticalOffset = pageRow * (originalPdf.height + VERTICAL_SPACING) * scaleY;
        const horizontalOffset = pageColumn * (originalPdf.width + HORIZONTAL_SPACING) * scaleX;

        const pageCustomName = `${nameWithoutExtension} - Page ${page}`;

        const { id, pdf } = placePdf(
          {
            x: originalPdf.x + horizontalOffset,
            y: originalPdf.y + verticalOffset,
          },
          originalPdf.fileId || null,
          "completed", // Use completed status since file is already uploaded
          originalPdf.width,
          originalPdf.height,
          totalPages,
          page, // Set pageToDisplay to lock to this specific page
          pageCustomName // Add custom name for the split page
        );

        // Copy other properties from original PDF
        const newPdf: Pdf = {
          ...pdf,
          rotate: originalPdf.rotate,
          scaleX: originalPdf.scaleX,
          scaleY: originalPdf.scaleY,
        };

        newPdfElements.push({
          id: `${consts.CANVAS_ELEMENTS.PDF}-${id}`,
          element: newPdf,
        });
        addedElementIds.push(`${consts.CANVAS_ELEMENTS.PDF}-${id}`);
      }

      // Add all new elements in a single undoable operation
      undoRedoStack?.addAction({
        do: () => {
          for (const { id, element } of newPdfElements) {
            reflect?.mutate.putElement({ id, element });
          }
        },
        undo: () => {
          for (const { id } of newPdfElements) {
            reflect?.mutate.deleteElement(id);
          }
        },
      });

      // Wait for the elements to be added to the stage, a bit of a hack to ensure the elements are added to the stage before we center the stage on them.
      setTimeout(() => {
        // Center the stage on the added elements
        if (stageRef?.current) {
          const viewBox = fitToSelectedElements(stageRef, addedElementIds);
          if (viewBox) {
            stageRef.current.onChangePosScale(viewBox);
            canvasUtils?.setSelectedElementIds(addedElementIds);
          }
        }
      }, 100);

      // Successfully split PDF into pages
    } catch (error) {
      console.error("Failed to split PDF pages:", error);
    } finally {
      setIsSplitting(false);
    }
  }

  async function handleDownload() {
    const fileId = elements[0]?.fileId;
    if (!fileId) {
      return;
    }
    const pdf = await pdfCacheService.getPDF(fileId.toString(), metadata?.url);
    const pdfData = await pdf.saveDocument();
    const blob = new Blob([new Uint8Array(pdfData)], { type: "application/pdf" });

    // Use custom name if available, otherwise fall back to metadata fileName
    let downloadName = elements[0]?.customName || metadata?.fileName || "downloaded.pdf";
    if (!downloadName.endsWith(".pdf")) {
      downloadName += ".pdf";
    }
    downloadFileUsingBlob(blob, downloadName);
  }

  if (elements.length !== 1) {
    return null;
  }

  const PageNavigation = () => {
    if (pageToDisplay) {
      return null;
    }
    return (
      <ButtonGroup>
        <div className={style.container}>
          <div className={style.pageNavigationContainer}>
            <Button
              className={style.pageNavigationButton}
              onClick={handlePreviousPage}
              disabled={currentPage <= 1}
              title={"Previous page"}
            >
              <ChevronLeft color="currentColor" />
            </Button>

            <div className={style.pageIndicator}>
              <>
                <input
                  ref={inputRef}
                  type="number"
                  className={style.pageInput}
                  value={pageInputValue}
                  onChange={handlePageInputChange}
                  onFocus={handlePageInputFocus}
                  min="1"
                  max={totalPages}
                />
                <span className={style.pageSeparator}>/</span>
                <span className={style.totalPages}>{totalPages}</span>
              </>
            </div>

            <Button
              className={style.pageNavigationButton}
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
              title={"Next page"}
            >
              <ChevronRight color="currentColor" />
            </Button>
          </div>
        </div>
      </ButtonGroup>
    );
  };

  return (
    <>
      {elements.length === 1 && (
        <ButtonGroup>
          <div className={style.container}>
            <input
              id="pdf-name-input"
              ref={nameInputRef}
              type="text"
              className={style.pdfNameInput}
              value={nameValue}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              onFocus={handleNameFocus}
              onKeyPress={handleNameKeyPress}
              maxLength={255}
              title={nameValue}
              placeholder="PDF Name..."
            />
          </div>
        </ButtonGroup>
      )}
      <PageNavigation />
      {totalPages > 1 && !pageToDisplay && (
        <ButtonGroup>
          <div className={style.container}>
            <Button
              ref={splitPagesButtonRef}
              className={style.pdfActionButton}
              onClick={splitPDFPages}
              disabled={isSplitting}
              tooltip={"Extract pages"}
            >
              <PdfPageSplitIcon size={20} strokeColor="currentColor" />
            </Button>
          </div>
        </ButtonGroup>
      )}
      <ButtonGroup>
        <div className={style.container}>
          <Button ref={downloadButtonRef} className={style.pdfActionButton} onClick={handleDownload} tooltip="Download">
            {newStyle ? <Icon icon="icon-download-pdf" /> : <DownloadIcon size={18} color="currentColor" />}
          </Button>
        </div>
      </ButtonGroup>
    </>
  );
}
